import React, { FC, useEffect, useState, useMemo } from 'react'
import { secToTwoDigitsHourMinute, digitsHourMinuteToNumberHourMinute } from '../../../../utils/date'
import Modal from '../../common/Modal'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

interface weeklyTrackTime {
  week_number: number
  track_start_time: string | null
  track_stop_time: string | null
}

interface Member {
  id: number
  name: string
  kana: string | null
  email: string
  code: string | null
  team_name: string
  profile_image: string | null
  use_common_setting: boolean
  change_authority: boolean
  weekly_track_times: weeklyTrackTime[]
}

interface RequestParams {
  commonFlg: boolean
  memberIds?: number[]
  changeAuthority?: boolean
  weeklyTrackTimes?: weeklyTrackTime[]
  useCommonSetting? : boolean
}

interface Props {
  targetMemberList: Member[]
  commonValues: weeklyTrackTime[]
  isDisplayModal: boolean
  modalType: string
  submitCallback: (data: RequestParams) => void
  closeCallback: VoidFunction
}

const AUTHORITY_LIST = [
  {
    label: "許可する",
    value: "on",
  },
  {
    label: "許可しない",
    value: "off",
  },
]

const DISPLAY_MODAL_MEMBER_LIMIT = 30

const EditModal: FC<Props> = (props) => {
  const {
    targetMemberList,
    commonValues,
    isDisplayModal,
    modalType,
    submitCallback,
    closeCallback,
  } = props

  const [weeklyTrackTime, setWeeklyTrackTime] = useState<weeklyTrackTime[]>(
    Array(7).fill(null).map((_v, i) => ({ week_number: i, track_start_time: "00:00", track_stop_time: "24:00" })),
  )

  const [limitTrackStartWeekNumbers, setLimitTrackStartWeekNumbers] = useState<number[]>([])
  const [limitTrackEndWeekNumbers, setLimitTrackEndWeekNumbers] = useState<number[]>([])
  const [noTrackWeekNumbers, setNoTrackWeekNumbers] = useState<number[]>([])

  const [selectTypeList, setSelectTypeList] = useState<string[]>(Array(7).fill(null).map(() => "all"))
  const [authorityType, setAuthorityType] = useState<string>("off")

  const [isUseCommon, setIsUseCommon] = useState<boolean>(false)

  const [isTrackTime, setIsTrackTime] = useState<boolean>(true)
  const [isAuthority, setIsAuthority] = useState<boolean>(false)

  const weekDayDisplay = (num: number): string => {
    const weekArray = ["日", "月", "火", "水", "木", "金", "土"]
    return `${weekArray[num]}曜日`
  }

  const weekDayColor = (num: number): string => {
    if (num === 0) {
      return "#FF0000"
    }
    if (num === 6) {
      return "#317AB7"
    }
    return "#000000"
  }

  const hourList = (selectType: string, weekNumber: number) => {
    if (selectType === "start") {
      const stopTimeObj = digitsHourMinuteToNumberHourMinute(weeklyTrackTime[weekNumber]?.track_stop_time || "24:00")
      const tmpHourCount = stopTimeObj.hour > 24 ? 24 : stopTimeObj.hour
      return (
        Array((tmpHourCount) + 1).fill(null).map((_v, i) => (
          <option value={i} key={`track_start_hour ${i + 0}`}>{i}</option>
        ))
      )
    }
    const startTimeObj = digitsHourMinuteToNumberHourMinute(weeklyTrackTime[weekNumber]?.track_start_time || "00:00")
    return (
      Array(24 - startTimeObj.hour + 1).fill(null).map((_v, i) => (
        <option value={i + startTimeObj.hour} key={`track_end_hour ${i + startTimeObj.hour}`}>{i + startTimeObj.hour}</option>
      ))
    )
  }

  const minuteList = (selectType: string, weekNumber: number) => {
    const splitMinuteNumber = 5
    const startTimeObj = digitsHourMinuteToNumberHourMinute(weeklyTrackTime[weekNumber]?.track_start_time || "00:00")
    const stopTimeObj = digitsHourMinuteToNumberHourMinute(weeklyTrackTime[weekNumber]?.track_stop_time || "24:00")
    if (selectType === "start") {
      if (startTimeObj.hour === stopTimeObj.hour) {
        const minuteCount = Math.floor(Number(stopTimeObj.minute / splitMinuteNumber))
        return (
          Array(minuteCount + 1).fill(null).map((_v, i) => (
            <option value={i * splitMinuteNumber} key={`track_start_minute ${i * splitMinuteNumber}`}>
              {`00${i * splitMinuteNumber}`.slice(-2)}
            </option>
          ))
        )
      }
      return (
        Array(60 / splitMinuteNumber).fill(null).map((_v, i) => (
          <option value={i * splitMinuteNumber} key={`track_start_minute ${i * splitMinuteNumber + 1}`}>
            {`00${i * splitMinuteNumber}`.slice(-2)}
          </option>
        ))
      )
    }
    if (stopTimeObj.hour > 23) {
      return (
        <option value={0}>00</option>
      )
    }
    if (startTimeObj.hour === stopTimeObj.hour) {
      const startMinuteCount = startTimeObj.minute
      return (
        Array((60 - startMinuteCount) / splitMinuteNumber).fill(null).map((_v, i) => (
          <option value={i * splitMinuteNumber + startMinuteCount} key={`track_end_minute ${i * splitMinuteNumber + startMinuteCount}`}>
            {`00${i * splitMinuteNumber + startMinuteCount}`.slice(-2)}
          </option>
        ))
      )
    }

    return (
      Array(60 / splitMinuteNumber).fill(null).map((_v, i) => (
        <option value={i * splitMinuteNumber} key={`track_end_minute ${i * splitMinuteNumber + 1}`}>
          {`00${i * splitMinuteNumber}`.slice(-2)}
        </option>
      ))
    )
  }

  const startHoursList = useMemo(() => Array(7).fill(null).map((_v, i) => hourList("start", i)), [weeklyTrackTime])
  const startMinutesList = useMemo(() => Array(7).fill(null).map((_v, i) => minuteList("start", i)), [weeklyTrackTime])

  const endHoursList = useMemo(() => Array(7).fill(null).map((_v, i) => hourList("end", i)), [weeklyTrackTime])
  const endMinutesList = useMemo(() => Array(7).fill(null).map((_v, i) => minuteList("end", i)), [weeklyTrackTime])

  const changeWeeklyStartTrackTime = (hour: number, minute: number, weekNumber: number) => {
    const tmpSec = hour * 60 * 60 + minute * 60
    const stopTimeObj = digitsHourMinuteToNumberHourMinute(weeklyTrackTime[weekNumber].track_stop_time || "24:00")
    if (tmpSec > stopTimeObj.hour * 60 * 60 + stopTimeObj.minute * 60) {
      setWeeklyTrackTime((cur) => cur.map((v, i) => (
        i === weekNumber ? {
          week_number: weekNumber,
          track_start_time: v.track_stop_time,
          track_stop_time: v.track_stop_time,
        } : v
      )))
    } else {
      setWeeklyTrackTime((cur) => cur.map((v, i) => (
        i === weekNumber ? {
          week_number: weekNumber,
          track_start_time: secToTwoDigitsHourMinute(tmpSec),
          track_stop_time: v.track_stop_time,
        } : v
      )))
    }
  }

  const changeWeeklyEndTrackTime = (hour: number, minute: number, weekNumber: number) => {
    const tmpSec = hour * 60 * 60 + minute * 60
    const startTimeObj = digitsHourMinuteToNumberHourMinute(weeklyTrackTime[weekNumber].track_start_time || "00:00")
    if (tmpSec < startTimeObj.hour * 60 * 60 + startTimeObj.minute * 60) {
      setWeeklyTrackTime((cur) => cur.map((v, i) => (
        i === weekNumber ? {
          week_number: weekNumber,
          track_start_time: v.track_start_time,
          track_stop_time: v.track_start_time,
        } : v
      )))
    } else if (tmpSec >= 24 * 60 * 60 - 1) {
      setWeeklyTrackTime((cur) => cur.map((v, i) => (
        i === weekNumber ? {
          week_number: weekNumber,
          track_start_time: v.track_start_time,
          track_stop_time: "24:00",
        } : v
      )))
    } else {
      setWeeklyTrackTime((cur) => cur.map((v, i) => (
        i === weekNumber ? {
          week_number: weekNumber,
          track_start_time: v.track_start_time,
          track_stop_time: secToTwoDigitsHourMinute(tmpSec),
        } : v
      )))
    }
  }

  const close = () => {
    setLimitTrackStartWeekNumbers([])
    setLimitTrackEndWeekNumbers([])
    setNoTrackWeekNumbers([])
    setSelectTypeList(Array(7).fill(null).map(() => "all"))
    closeCallback()
    setIsAuthority(false)
    setIsTrackTime(true)
    setIsUseCommon(false)
  }

  const submit = () => {
    const tmpWeeklyTimeData = weeklyTrackTime.map((v) => (
      noTrackWeekNumbers.includes(v.week_number) ? {
        week_number: v.week_number,
        track_start_time: "00:00",
        track_stop_time: "00:00",
      } : {
        week_number: v.week_number,
        track_start_time: v.track_start_time === "00:00" ? null : v.track_start_time,
        track_stop_time: v.track_stop_time === "24:00" ? null : v.track_stop_time,
      }
    ))
    if (targetMemberList.length) {
      const params: RequestParams = {
        commonFlg: false,
        memberIds: targetMemberList.map((member) => member.id),
      }
      if (isAuthority) params.changeAuthority = authorityType === "on"
      if (isTrackTime && !isUseCommon) params.weeklyTrackTimes = tmpWeeklyTimeData
      if (isTrackTime) params.useCommonSetting = isUseCommon
      submitCallback(params)
    } else {
      const params: RequestParams = {
        commonFlg: true,
        useCommonSetting: isUseCommon,
      }
      if (!isUseCommon) params.weeklyTrackTimes = tmpWeeklyTimeData
      submitCallback(params)
    }
    close()
  }

  const changeTrackTime = (startTime: string, endTime: string, weekNumber: number) => {
    setWeeklyTrackTime((cur) => cur.map((v, i) => (
      i === weekNumber ? {
        week_number: weekNumber,
        track_start_time: startTime,
        track_stop_time: endTime,
      } : v
    )))
  }

  const selectTrackType = (weekNum: number, type: string) => {
    if (type === "all") {
      setLimitTrackStartWeekNumbers((cur) => cur.filter((value) => value !== weekNum))
      setLimitTrackEndWeekNumbers((cur) => cur.filter((value) => value !== weekNum))
      setNoTrackWeekNumbers((cur) => cur.filter((value) => value !== weekNum))
      changeTrackTime("00:00", "24:00", weekNum)
    }
    if (type === "none") {
      setLimitTrackStartWeekNumbers((cur) => cur.filter((value) => value !== weekNum))
      setLimitTrackEndWeekNumbers((cur) => cur.filter((value) => value !== weekNum))
      setNoTrackWeekNumbers((cur) => Array.from(new Set([...cur, weekNum])))
      changeTrackTime("00:00", "00:00", weekNum)
    }
    if (type === "start") {
      setLimitTrackStartWeekNumbers((cur) => Array.from(new Set([...cur, weekNum])))
      setLimitTrackEndWeekNumbers((cur) => cur.filter((value) => value !== weekNum))
      setNoTrackWeekNumbers((cur) => cur.filter((value) => value !== weekNum))
      changeTrackTime("09:00", "24:00", weekNum)
    }
    if (type === "stop") {
      setLimitTrackStartWeekNumbers((cur) => cur.filter((value) => value !== weekNum))
      setLimitTrackEndWeekNumbers((cur) => Array.from(new Set([...cur, weekNum])))
      setNoTrackWeekNumbers((cur) => cur.filter((value) => value !== weekNum))
      changeTrackTime("00:00", "18:00", weekNum)
    }
    if (type === "custom") {
      setLimitTrackStartWeekNumbers((cur) => Array.from(new Set([...cur, weekNum])))
      setLimitTrackEndWeekNumbers((cur) => Array.from(new Set([...cur, weekNum])))
      setNoTrackWeekNumbers((cur) => cur.filter((value) => value !== weekNum))
      changeTrackTime("09:00", "18:00", weekNum)
    }
    setSelectTypeList((cur) => cur.map((v, i) => (i === weekNum ? type : v)))
  }

  const buildTrackList = (targetValues: weeklyTrackTime[]) => {
    const tmpNoTrack: number[] = []
    const tmpTrackStart: number[] = []
    const tmpTrackEnd: number[] = []
    const tmpSelectType: string[] = []
    targetValues.forEach((v) => {
      if (
        v.track_start_time !== null && v.track_stop_time !== null && v.track_start_time === v.track_stop_time
      ) {
        tmpNoTrack.push(v.week_number)
        tmpSelectType.push("none")
      } else if (v.track_start_time && v.track_stop_time === null) {
        tmpTrackStart.push(v.week_number)
        tmpSelectType.push("start")
      } else if (v.track_stop_time && v.track_start_time === null) {
        tmpTrackEnd.push(v.week_number)
        tmpSelectType.push("stop")
      } else if (v.track_start_time === null && v.track_stop_time === null) {
        tmpSelectType.push("all")
      } else {
        tmpTrackStart.push(v.week_number)
        tmpTrackEnd.push(v.week_number)
        tmpSelectType.push("custom")
      }
    })
    setWeeklyTrackTime(targetValues)
    setSelectTypeList(tmpSelectType)
    setNoTrackWeekNumbers(tmpNoTrack)
    setLimitTrackStartWeekNumbers(tmpTrackStart)
    setLimitTrackEndWeekNumbers(tmpTrackEnd)
  }

  const changeUseCommon = (useCommon: boolean) => {
    setIsUseCommon(useCommon)
    if (useCommon) {
      buildTrackList(commonValues)
    } else {
      buildTrackList(targetMemberList[0].weekly_track_times.length ? targetMemberList[0].weekly_track_times : commonValues)
    }
  }

  useEffect(() => {
    if (!isDisplayModal) return
    let targetValues = []
    if (targetMemberList[0] && !targetMemberList[0].use_common_setting) {
      targetValues = targetMemberList[0].weekly_track_times
    } else {
      targetValues = commonValues
    }
    buildTrackList(targetValues)
    setIsUseCommon(targetMemberList[0]?.use_common_setting || false)
    setAuthorityType(targetMemberList[0]?.change_authority ? "on" : "off")
  }, [isDisplayModal, commonValues, targetMemberList])

  return (
    <Modal
      show={isDisplayModal}
      wrapperClass="w900"
      closeCallback={close}
      closeable
    >
      <>
        <div
          style={{
            textAlign: "center",
            fontSize: "20px",
            marginBottom: "16px",
          }}
        >
          計測時間設定
        </div>
        <div
          style={{
            display: "flex",
            flexFlow: "column",
            gap: "16px",
          }}
        >
          {
            !!targetMemberList.length && modalType === "multi" && (
              <>
                <div
                  style={{
                    border: "solid 1px #999999",
                    borderRadius: "4px",
                    padding: "8px",
                    display: "flex",
                    flexFlow: "column",
                    gap: "8px",
                    backgroundColor: "#F3F3f3",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      fontSize: "14px",
                    }}
                  >
                    対象
                  </div>
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "4px",
                    }}
                  >
                    {
                      targetMemberList.slice(0, DISPLAY_MODAL_MEMBER_LIMIT).map((v) => (
                        <div
                          key={`member_${v.id}`}
                          style={{
                            border: "solid 1px #999999",
                            borderRadius: "20px",
                            padding: "2px 8px",
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                            fontSize: "10px",
                            backgroundColor: "#FFFFFF",
                          }}
                        >
                          <img
                            alt={v.name || ""}
                            src={v.profile_image ?? `/static/images/profile/${(Number(v.id) || 0) % 12}.jpeg`}
                            style={{ width: "16px", height: "16px", borderRadius: "50%" }}
                          />
                          {v.name}
                        </div>
                      ))
                    }
                    {
                      targetMemberList.length > DISPLAY_MODAL_MEMBER_LIMIT && (
                        <div
                          style={{
                            padding: "3px",
                          }}
                        >
                          {`…他${targetMemberList.length - DISPLAY_MODAL_MEMBER_LIMIT}名`}
                        </div>
                      )
                    }
                  </div>
                </div>
                <div
                  style={{
                    border: "solid 1px #999999",
                    borderRadius: "4px",
                    padding: "8px",
                    display: "flex",
                    flexFlow: "column",
                    gap: "8px",
                    backgroundColor: "#FFFFFF",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      fontSize: "14px",
                    }}
                  >
                    個人編集許可
                    <button
                      type="button"
                      onClick={() => setIsAuthority((cur) => !cur)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                        padding: "0",
                        fontSize: "10px",
                        color: isAuthority ? "#008800" : "#666666",
                      }}
                    >
                      <FontAwesomeIcon icon={['far', isAuthority ? 'check-square' : 'square']} />
                      設定を変更する
                    </button>
                  </div>
                  {
                    isAuthority && (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                        }}
                      >
                        個人による編集を…
                        <select
                          style={{
                            border: "solid 1px #999999",
                            padding: "4px",
                            borderRadius: "4px",
                            appearance: "auto",
                            cursor: "pointer",
                            backgroundColor: "#ffffff",
                          }}
                          onChange={(e) => setAuthorityType(e.target.value)}
                          value={authorityType}
                        >
                          {
                            AUTHORITY_LIST.map((v) => (
                              <option key={`select_type_${v.value}`} value={v.value}>
                                {v.label}
                              </option>
                            ))
                          }
                        </select>
                      </div>
                    )
                  }
                </div>
              </>
            )
          }
          <div
            style={{
              border: "solid 1px #999999",
              borderRadius: "4px",
              padding: "8px",
              display: "flex",
              flexFlow: "column",
              gap: "8px",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                fontSize: "14px",
              }}
            >
              計測時間設定
              {
                (!!targetMemberList.length && modalType === "multi") && (
                  <button
                    type="button"
                    onClick={() => setIsTrackTime((cur) => !cur)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                      padding: "0",
                      fontSize: "10px",
                      color: isTrackTime ? "#008800" : "#666666",
                    }}
                  >
                    <FontAwesomeIcon icon={['far', isTrackTime ? 'check-square' : 'square']} />
                    設定を変更する
                  </button>
                )
              }
            </div>
            {
              isTrackTime && (
                <>
                  {
                    !!targetMemberList.length && (
                      <div>
                        <button
                          type="button"
                          onClick={() => changeUseCommon(!isUseCommon)}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "4px",
                            padding: "4px",
                            fontSize: "12px",
                            color: isUseCommon ? "#008800" : "#666666",
                            border: `1px solid ${isUseCommon ? "#008800" : "#666666"}`,
                            borderRadius: "4px",
                            width: "100%",
                            backgroundColor: isUseCommon ? "#e7ffe7" : "#FFFFFF",
                          }}
                        >
                          <FontAwesomeIcon icon={['far', isUseCommon ? 'check-square' : 'square']} />
                          会社全体の設定に準拠する
                        </button>
                      </div>
                    )
                  }
                  <div
                    style={{
                      position: "relative",
                    }}
                  >
                    {
                      isUseCommon && (
                        <div
                          style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            backgroundColor: "rgba(255, 255, 255, 0.8)",
                            zIndex: 1,
                          }}
                        />
                      )
                    }
                    <table className="setting_weekly_track_time_table">
                      <thead>
                        <tr>
                          <th>曜日</th>
                          <th>計測</th>
                          <th>計測開始時刻</th>
                          {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
                          <th />
                          <th>計測終了時刻</th>
                        </tr>
                      </thead>
                      <tbody>
                        {
                          weeklyTrackTime && weeklyTrackTime.map((v) => (
                            <tr key={v.week_number}>
                              <td
                                style={{
                                  color: weekDayColor(v.week_number),
                                }}
                              >
                                {weekDayDisplay(v.week_number)}
                              </td>
                              <td>
                                <select
                                  style={{
                                    border: "solid 1px #999999",
                                    borderRadius: "4px",
                                    padding: "0.4em 0.8em",
                                    appearance: "auto",
                                    cursor: "pointer",
                                    backgroundColor: "#FFFFFF",
                                  }}
                                  onChange={(e) => selectTrackType(v.week_number, e.target.value)}
                                  value={selectTypeList[v.week_number]}
                                >
                                  <option value="all">
                                    終日計測する
                                  </option>
                                  <option value="none">
                                    計測しない
                                  </option>
                                  <option value="start">
                                    開始時間を設定する
                                  </option>
                                  <option value="stop">
                                    終了時間を設定する
                                  </option>
                                  <option value="custom">
                                    開始・終了時間を設定する
                                  </option>
                                </select>
                              </td>
                              {
                                !["all", "none"].includes(selectTypeList[v.week_number]) && (
                                  <td>
                                    <div>
                                      {
                                        limitTrackStartWeekNumbers.includes(v.week_number) ? (
                                          <div>
                                            <div className="start_track_time">
                                              <select
                                                onChange={(e) => {
                                                  changeWeeklyStartTrackTime(
                                                    Number(e.target.value),
                                                    digitsHourMinuteToNumberHourMinute(v.track_start_time || "00:00").minute,
                                                    v.week_number,
                                                  )
                                                }}
                                                value={digitsHourMinuteToNumberHourMinute(v.track_start_time || "00:00").hour}
                                              >
                                                {startHoursList[v.week_number]}
                                              </select>
                                              :
                                              <select
                                                onChange={(e) => {
                                                  changeWeeklyStartTrackTime(
                                                    digitsHourMinuteToNumberHourMinute(v.track_start_time || "00:00").hour,
                                                    Number(e.target.value), v.week_number,
                                                  )
                                                }}
                                                value={digitsHourMinuteToNumberHourMinute(v.track_start_time || "00:00").minute}
                                              >
                                                {startMinutesList[v.week_number]}
                                              </select>
                                            </div>
                                          </div>
                                        ) : (
                                          <div style={{ fontSize: "20px", color: "grey" }}>0:00</div>
                                        )
                                      }
                                    </div>
                                  </td>
                                )
                              }
                              <td
                                colSpan={["all", "none"].includes(selectTypeList[v.week_number]) ? 3 : 1}
                                style={{
                                  width: ["all", "none"].includes(selectTypeList[v.week_number]) ? "55%" : "5%",
                                }}
                              >
                                {
                                  selectTypeList[v.week_number] === "all" && (
                                    <div
                                      style={{
                                        display: "flex",
                                        justifyContent: "center",
                                        fontSize: "large",
                                        color: "#0f9655",
                                        fontWeight: "bold",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      終日計測する
                                    </div>
                                  )
                                }
                                {
                                  selectTypeList[v.week_number] === "none" && (
                                    <div
                                      style={{
                                        display: "flex",
                                        justifyContent: "center",
                                        fontSize: "large",
                                        color: "red",
                                        fontWeight: "bold",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      計測しない
                                    </div>
                                  )
                                }
                                {
                                  !["all", "none"].includes(selectTypeList[v.week_number]) && (
                                    <div
                                      style={{
                                        fontSize: "18px",
                                      }}
                                    >
                                      ~
                                    </div>
                                  )
                                }
                              </td>
                              {
                                !["all", "none"].includes(selectTypeList[v.week_number]) && (
                                  <td>
                                    <div>
                                      {
                                        limitTrackEndWeekNumbers.includes(v.week_number) ? (
                                          <div>
                                            <div className="end_track_time">
                                              <select
                                                onChange={(e) => {
                                                  changeWeeklyEndTrackTime(
                                                    Number(e.target.value),
                                                    digitsHourMinuteToNumberHourMinute(v.track_stop_time || "24:00").minute,
                                                    v.week_number,
                                                  )
                                                }}
                                                value={digitsHourMinuteToNumberHourMinute(v.track_stop_time || "24:00").hour}
                                              >
                                                {endHoursList[v.week_number]}
                                              </select>
                                              :
                                              <select
                                                onChange={(e) => {
                                                  changeWeeklyEndTrackTime(
                                                    digitsHourMinuteToNumberHourMinute(v.track_stop_time || "24:00").hour,
                                                    Number(e.target.value),
                                                    v.week_number,
                                                  )
                                                }}
                                                value={digitsHourMinuteToNumberHourMinute(v.track_stop_time || "24:00").minute}
                                              >
                                                {endMinutesList[v.week_number]}
                                              </select>
                                            </div>
                                          </div>
                                        ) : (
                                          <div style={{ fontSize: "20px", color: "grey" }}>24:00</div>
                                        )
                                      }
                                    </div>
                                  </td>
                                )
                              }
                            </tr>
                          ))
                        }
                      </tbody>
                    </table>
                  </div>
                </>
              )
            }
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: "16px",
              marginTop: "32px",
            }}
          >
            <button
              type="button"
              style={{
                border: `solid 1px #999999`,
                borderRadius: "4px",
                backgroundColor: "#EEEEEE",
                color: "#666666",
                minWidth: "130px",
                textAlign: "center",
              }}
              onClick={close}
            >
              キャンセル
            </button>
            <button
              type="button"
              style={{
                border: `solid 1px #507DC0`,
                borderRadius: "4px",
                backgroundColor: (!isAuthority && !isTrackTime) ? "#EEEEEE" : "#507DC0",
                color: (!isAuthority && !isTrackTime) ? "#666666" : "#FFFFFF",
                minWidth: "130px",
                textAlign: "center",
              }}
              onClick={submit}
              disabled={!isAuthority && !isTrackTime}
            >
              設定する
            </button>
          </div>
        </div>
      </>
    </Modal>
  )
}

export default EditModal
